import React, { useState, useEffect } from 'react';
import { Package2, Search, MapPin, CalendarClock, Truck, CheckCircle2, Circle, Package, IndianRupee, Clock, Phone, User, DollarSign, Box, MapPinned, Calendar } from 'lucide-react';
import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { useMyContext } from '../../../../context/MyContext';
import { AlertCircle, ClipboardList, PackageSearch } from 'lucide-react';
import '../../../../css/custom-scrollbar.css';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Base_Url, track } from '../../../../config/config';
import FullPageLoader from '../../../../component/fullPageLoader/fullPageLoader';


const OrderTracking = () => {
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [waybillNumber, setWaybillNumber] = useState('');
  const [orderTrackingInfo, setOrderTrackingInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { isDarkMode, isToggleSidebar, orderid, setorderId } = useMyContext();

  useEffect(() => {
    if (orderid) {
      handleTrack();
    }
  }, [orderid]);


  const handleTrack = async () => {
    // Get the tracking number - prioritize waybillNumber if it exists, otherwise use orderid
    const trackingNumber = (waybillNumber || orderid)?.toString().trim();

    if (!trackingNumber) {
      setError('Please enter a valid order ID');
      setOrderTrackingInfo(null);
      return;
    }


    setIsLoading(true);
    setIsPageLoading(true);
    setError('');

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    try {
      const requestPayload = {
        order_id: trackingNumber, // Use the determined tracking number
        shipment_type: 'Forward'
      };
      const response = await axios.post(Base_Url + track, requestPayload, {
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.status === "SUCCESS") {
        setOrderTrackingInfo(response.data.data.ShipmentData[0].Shipment);
        setError('');
        setIsPageLoading(false);
        // Only clear orderid from context if we successfully used it
        if (orderid && !waybillNumber) {
          setorderId(null);
        }
      }
      else {
        setError(response.data.message);
        setIsPageLoading(false);
      }
    } catch (err) {
      console.error('Tracking error:', err.response?.data || err.message); // Debug log
      setError('Failed to fetch tracking information. Please try again.');
      setOrderTrackingInfo(null);
      setIsPageLoading(false);
    } finally {
      setIsLoading(false);
      setIsPageLoading(false);
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return "Not Available";
    return new Date(dateString).toLocaleString();
  };

  const DetailRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
      <div className="flex items-center gap-2">
        {Icon && (
          <div className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
            <Icon className="w-3.5 h-3.5 text-gray-400" />
          </div>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-800 dark:text-white text-right">{value || "Not Available"}</span>
    </div>
  );

  const getStepIcon = (scan, isLatest) => {
    const status = scan.ScanDetail.Scan.toLowerCase() || scan?.ScanDetail?.ScanType;
    ;
    if (status) {
      return (
        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center ring-4 ring-emerald-50 dark:ring-emerald-900/20">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        </div>
      );
    }
    if (isLatest) {
      return (
        <div className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/30 animate-pulse">
          <Truck className="w-4 h-4 text-white" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <Circle className="w-3.5 h-3.5 text-gray-400" />
      </div>
    );
  };

  // Reusable section card wrapper with gradient icon badge header
  const InfoCard = ({ icon: Icon, title, gradient, children }) => (
    <div className={`rounded-2xl border transition-shadow hover:shadow-lg overflow-hidden ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
      <div className="flex items-center gap-3 px-4 pt-4 pb-3">
        <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h2 className="text-sm font-bold text-gray-800 dark:text-white">{title}</h2>
      </div>
      <div className="px-4 pb-4">
        {children}
      </div>
    </div>
  );

  const DeliveryInfoPanel = () => (
    <div className="space-y-4">
      {/* Shipment Overview Card */}
      <InfoCard icon={Package2} title="Shipment Overview" gradient="from-purple-500 to-fuchsia-600">
        <DetailRow label="AWB Number" value={orderTrackingInfo?.AWB} icon={Package} />
        <DetailRow label="Order ID" value={orderTrackingInfo?.ReferenceNo} icon={Package2} />
        <DetailRow label="Order Type" value={orderTrackingInfo?.OrderType} icon={Box} />
        <DetailRow label="Invoice Amount" value={`₹${orderTrackingInfo?.InvoiceAmount || 0}`} icon={IndianRupee} />
        <DetailRow label="COD Amount" value={`₹${orderTrackingInfo?.CODAmount || 0}`} icon={IndianRupee} />
        <DetailRow label="Quantity" value={orderTrackingInfo?.Quantity} icon={Box} />
      </InfoCard>

      {/* Location Details */}
      <InfoCard icon={MapPinned} title="Location Details" gradient="from-sky-500 to-blue-600">
        <DetailRow label="Origin" value={orderTrackingInfo?.Origin} icon={MapPin} />
        <DetailRow label="Destination" value={orderTrackingInfo?.Destination || "Not specified"} icon={MapPin} />
        <DetailRow label="Pickup Location" value={orderTrackingInfo?.PickupLocation} icon={MapPin} />
      </InfoCard>

      {/* Consignee Details */}
      <InfoCard icon={User} title="Consignee Details" gradient="from-emerald-500 to-teal-600">
        {orderTrackingInfo?.Consignee && (
          <>
            <DetailRow label="Name" value={orderTrackingInfo.Consignee.Name} icon={User} />
            <DetailRow label="City" value={orderTrackingInfo.Consignee.City || orderTrackingInfo.Consignee.State} icon={MapPin} />
            <DetailRow label="Pin Code" value={orderTrackingInfo.Consignee.PinCode} icon={MapPin} />
            <DetailRow label="Country" value={orderTrackingInfo.Consignee.Country} icon={MapPin} />
          </>
        )}
      </InfoCard>

      {/* Dates Information */}
      <InfoCard icon={Calendar} title="Important Dates" gradient="from-amber-500 to-orange-600">
        <DetailRow label="Pickup Date" value={formatDate(orderTrackingInfo?.PickUpDate)} icon={Calendar} />
        <DetailRow label="Expected Delivery" value={formatDate(orderTrackingInfo?.ExpectedDeliveryDate)} icon={Calendar} />
        <DetailRow label="First Attempt Date" value={formatDate(orderTrackingInfo?.FirstAttemptDate)} icon={Calendar} />
      </InfoCard>

      {/* Status Banner */}
      <div className="relative p-5 rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-700 shadow-lg shadow-purple-500/20 overflow-hidden">
        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
        <div className="absolute -right-2 -bottom-8 w-20 h-20 rounded-full bg-white/10" />
        <div className="relative flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
            <Clock className="w-4 h-4 text-white" />
          </div>
          <h3 className="font-bold text-white text-sm">Current Status</h3>
        </div>
        <div className="relative space-y-1.5">
          <p className="text-base font-bold text-white">
            {orderTrackingInfo?.Status?.Status}
          </p>
          <p className="text-xs text-white/80 leading-relaxed">
            {orderTrackingInfo?.Status?.Instructions}
          </p>
          <p className="text-[11px] text-white/60 pt-1">
            Last Updated: {formatDate(orderTrackingInfo?.Status?.StatusDateTime)}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-gray-50'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div className="fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20"
            style={window.innerWidth < 768 ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' } : {}}>
            <Sidebar />
          </div>
        )}

        <FullPageLoader
          isLoading={isPageLoading}
          message="Processing your request..."
        />

        <div className="flex-1 p-4 md:p-8 overflow-auto bg-gray-100 custom-scrollbar">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {/* Main Tracking Section */}
            <div className={`lg:col-span-2 rounded-2xl border overflow-hidden h-fit ${isDarkMode ? "bg-gray-900/60 border-gray-800 shadow-black/20" : "bg-white border-gray-100 shadow-sm"}`}>
              {/* Gradient hero header */}
              <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 px-6 py-6 overflow-hidden">
                <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
                <div className="relative flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <Package2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-white/70 font-semibold mb-0.5">Live Tracking</p>
                    <h1 className="text-lg md:text-xl font-bold text-white">Track Your Shipment</h1>
                  </div>
                </div>
              </div>

              <div className="p-5 md:p-6">
                <div className="space-y-4">
                  {/* Search section */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Enter Order ID"
                        onChange={(e) => setWaybillNumber(e.target.value)}
                        className={`w-full pl-10 pr-4 py-2.5 rounded-xl border outline-none transition-all shadow-sm focus:shadow-md focus:ring-4 text-sm ${isDarkMode
                            ? "bg-gray-800/70 border-gray-700 text-white placeholder-gray-500 focus:border-indigo-400 focus:ring-indigo-500/20"
                            : "bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-indigo-400 focus:ring-indigo-500/15"
                          }`}
                      />
                    </div>
                    <button
                      onClick={handleTrack}
                      disabled={isLoading}
                      className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-xl hover:shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-purple-500/20 disabled:opacity-60 disabled:hover:translate-y-0 flex items-center gap-2 font-semibold text-sm transition-all duration-200"
                    >
                      {isLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Search className="w-4 h-4" />
                          <span>Track</span>
                        </>
                      )}
                    </button>
                  </div>

                  {error && (
                    <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-100 dark:bg-red-900/20 dark:border-red-900/40">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                    </div>
                  )}

                  {!orderTrackingInfo && !error && (
                    <div className="flex flex-col items-center justify-center py-14 text-center">
                      <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                        <PackageSearch className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Enter an Order ID to see live tracking</p>
                    </div>
                  )}

                  {orderTrackingInfo && (
                    <div className="space-y-1 mt-6">
                      {orderTrackingInfo.Scans?.map((scan, index) => (
                        <div key={index} className="relative pb-6 last:pb-0">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 relative z-10">
                              {getStepIcon(scan, index === 0)}
                            </div>
                            <div className={`flex-1 space-y-1 rounded-xl px-3.5 py-2.5 ${index === 0 ? (isDarkMode ? 'bg-indigo-900/20' : 'bg-indigo-50/60') : ''}`}>
                              <p className="font-semibold text-sm dark:text-white text-gray-800">
                                {scan.ScanDetail.Instructions}
                              </p>
                              <p className="text-[11px] text-gray-400 font-medium">
                                {formatDate(scan.ScanDetail.StatusDateTime)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {scan.ScanDetail.ScannedLocation}
                              </p>
                            </div>
                          </div>
                          {index < orderTrackingInfo.Scans.length - 1 && (
                            <div className="absolute left-4 top-9 w-0.5 h-full bg-gradient-to-b from-gray-200 to-transparent dark:from-gray-700" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Info Panel */}
            <div className="lg:col-span-3">
              <DeliveryInfoPanel />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default OrderTracking;
